import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { createEnergyScene } from './energyScene';

/**
 * Ambient app background: a calm clean-energy landscape (turbines, solar array, hills) drawn on a
 * canvas and sunk under a full-screen frosted pane, so it reads as a soft wash of colour behind the
 * work rather than a picture competing with it.
 *
 * Kept out of the way of work:
 *  - slow motion only, and it eases to ~20% speed while a field is focused or the user is typing;
 *  - prefers-reduced-motion renders one still frame; reduced-transparency hides it behind a solid pane;
 *  - fixed behind everything, pointer-events: none.
 */
const isField = (el) =>
  !!el && el !== document.body &&
  (el.matches?.('input, textarea, select, [contenteditable="true"], [role="combobox"]') ?? false);

export default function AmbientBackground() {
  const canvasRef = useRef(null);
  const sceneRef = useRef(null);
  const [focusing, setFocusing] = useState(false);

  useEffect(() => {
    if (!canvasRef.current) return undefined;
    let scene = null;
    try {
      scene = createEnergyScene(canvasRef.current, {
        getAnchor: () => document.querySelector('[data-app-main]')?.getBoundingClientRect(),
      });
    } catch (e) {
      // A canvas failure must never take the app down; the frosted pane alone is a fine background.
      console.warn('Ambient background unavailable', e); // eslint-disable-line no-console
    }
    sceneRef.current = scene;
    return () => scene?.destroy();
  }, []);

  // Focus mode: a focused field or recent typing slows the scene and thickens the frost.
  useEffect(() => {
    let typing = 0;
    const update = () => {
      const on = isField(document.activeElement) || typing > 0;
      setFocusing(on);
      sceneRef.current?.setFocus(on);
    };
    const onKey = () => { clearTimeout(typing); typing = setTimeout(() => { typing = 0; update(); }, 2500); update(); };
    const onOut = () => setTimeout(update, 0);
    document.addEventListener('focusin', update);
    document.addEventListener('focusout', onOut);
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(typing);
      document.removeEventListener('focusin', update);
      document.removeEventListener('focusout', onOut);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  return (
    <div className={cn('ambient-bg', focusing && 'ambient-bg--focus')} aria-hidden="true">
      <div className="ambient-bg__sky" />
      <canvas ref={canvasRef} className="ambient-bg__scene" />
      <div className="ambient-bg__frost" />
    </div>
  );
}
